import React from 'react'
import { useTranslation } from 'react-i18next'
import { CMultiSelect } from '@coreui/react-pro'

export interface SymbolFilterOption {
  chartedSymbolTicker?: string | null
  bullSymbolTicker?: string | null
  bearSymbolTicker?: string | null
  assetClass: number
}

interface SymbolFilterProps {
  /** The strategies the rows name, repeats and all. */
  strategies: SymbolFilterOption[]
  /** Names an asset class, for the group its symbols sit under. */
  assetClassLabel: (assetClass: number) => string
  /** The column's value on each row must be the strategy's charted ticker. */
  onChange: (filterFn: (item: string) => boolean) => void
}

interface SymbolChoice {
  ticker: string
  assetClass: number
  legs: Set<string>
}

/**
 * Column filter for a strategy's symbol, grouped by asset class. Each option's label carries the legs its strategies
 * trade, so searching a leg such as AMZU finds AMZN.
 */
const SymbolFilter: React.FC<SymbolFilterProps> = ({ strategies, assetClassLabel, onChange }) => {
  const { t } = useTranslation('common')

  const symbols = new Map<string, SymbolChoice>()
  for (const strategy of strategies) {
    const ticker = strategy.chartedSymbolTicker
    if (!ticker) {
      continue
    }

    const choice = symbols.get(ticker) ?? { ticker, assetClass: strategy.assetClass, legs: new Set<string>() }
    for (const leg of [strategy.bullSymbolTicker, strategy.bearSymbolTicker]) {
      if (leg && leg !== ticker) {
        choice.legs.add(leg)
      }
    }
    symbols.set(ticker, choice)
  }

  const groups = new Map<string, SymbolChoice[]>()
  for (const choice of symbols.values()) {
    const group = assetClassLabel(choice.assetClass)
    groups.set(group, [...(groups.get(group) ?? []), choice])
  }

  const options = [...groups.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([group, choices]) => ({
      label: group,
      options: choices
        .sort((a, b) => a.ticker.localeCompare(b.ticker))
        .map((choice) => {
          const legs = [...choice.legs]
          return {
            value: choice.ticker,
            label: [choice.ticker, ...legs].join(' · '),
            ticker: choice.ticker,
            legs: legs.join(' · '),
          }
        }),
    }))

  return (
    <CMultiSelect
      size="sm"
      options={options}
      // A count, because a tag carries the whole label and a narrow column wraps it line by line.
      selectionType="counter"
      selectionTypeCounterText={t('filters.selected')}
      visibleItems={6}
      optionsTemplate={(option) => (
        <span>
          <span className="fw-semibold">{String(option.ticker)}</span>
          {option.legs ? <span className="small text-body-secondary ms-2">{String(option.legs)}</span> : null}
        </span>
      )}
      onChange={(selected) => {
        const chosen = selected.map((element) => String(element.value))
        onChange((item) => (chosen.length ? chosen.includes(item) : true))
      }}
    />
  )
}

export default SymbolFilter
